/**
 * RegionalLayersFetchDialog — on-demand fetch of public infrastructure
 * layers (ArcGIS REST + PeeringDB) within a user-specified radius.
 */
import React, { useMemo, useState } from 'react';
import { Layers, Loader2, X, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useStore } from '../../store';
import type { SiteEntity } from '../../types/site';
import {
  ARCGIS_LAYER_CATALOG,
  catalogEntriesByCategory,
} from '../../config/arcgisLayerCatalog';
import { CATEGORY_LABELS, CATEGORY_ORDER } from '../../config/siteLayerColors';
import {
  defaultEnabledCatalogIds,
  fetchRegionalLayers,
  DEFAULT_REGIONAL_RADIUS_MILES,
  formatRadiusMiles,
  REGIONAL_RADIUS_OPTIONS_MILES,
} from '../../services/arcgis/fetchRegionalLayers';
import { validateArcgisQueryUrl } from '../../lib/validateArcgisQueryUrl';
import { Button } from '../ui/button';

interface Props {
  site: SiteEntity;
  open: boolean;
  onClose: () => void;
}

function sourceLabel(source: string | undefined): string {
  if (source === 'arcgis-import') return 'ArcGIS';
  if (source === 'peeringdb-import') return 'PeeringDB';
  if (source === 'kmz-import') return 'KMZ';
  if (source === 'doc-derived') return 'Document extraction';
  if (source === 'landgate-import') return 'LandGate';
  return source ?? 'Unknown';
}

export function RegionalLayersFetchDialog({ site, open, onClose }: Props) {
  const fetchAndMerge = useStore(s => s.fetchAndMergeRegionalLayers);

  const [radiusMiles, setRadiusMiles] = useState<number>(DEFAULT_REGIONAL_RADIUS_MILES);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(defaultEnabledCatalogIds()),
  );
  const [customUrl, setCustomUrl] = useState('');
  const [customUrlValid, setCustomUrlValid] = useState<boolean | null>(null);
  const [customUrlError, setCustomUrlError] = useState<string | undefined>();
  const [preview, setPreview] = useState<{
    fetchedBySource: Record<string, number>;
    warnings: string[];
  } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  const grouped = useMemo(() => catalogEntriesByCategory(), []);

  if (!open) return null;

  const toggleEntry = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setPreview(null);
  };

  const validateCustom = () => {
    const result = validateArcgisQueryUrl(customUrl);
    setCustomUrlValid(result.ok);
    setCustomUrlError(result.error);
    setPreview(null);
    return result;
  };

  const customUrls = customUrl.trim() && customUrlValid ? [customUrl.trim()] : [];

  const runPreview = async () => {
    if (selectedIds.size === 0 && customUrls.length === 0) {
      toast.error('Select at least one catalog layer or provide a valid custom URL');
      return;
    }
    if (customUrl.trim() && customUrlValid !== true) {
      const v = validateCustom();
      if (!v.ok) {
        toast.error(v.error ?? 'Custom URL is invalid');
        return;
      }
    }

    setPreviewLoading(true);
    try {
      const result = await fetchRegionalLayers({
        site: {
          location: site.location,
          siteBoundary: site.siteBoundary,
          siteCentroid: site.siteCentroid,
        },
        catalogEntryIds: [...selectedIds],
        customUrls: customUrl.trim() && validateArcgisQueryUrl(customUrl).ok
          ? [validateArcgisQueryUrl(customUrl).normalizedUrl!]
          : undefined,
        radiusMiles,
      });
      setPreview({ fetchedBySource: result.fetchedBySource, warnings: result.warnings });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleFetch = async () => {
    if (selectedIds.size === 0 && !customUrl.trim()) {
      toast.error('Select at least one layer source');
      return;
    }
    if (customUrl.trim()) {
      const v = validateArcgisQueryUrl(customUrl);
      if (!v.ok) {
        toast.error(v.error ?? 'Custom URL is invalid');
        return;
      }
    }

    setFetching(true);
    try {
      const result = await fetchAndMerge(site.id, {
        catalogEntryIds: [...selectedIds],
        customUrls: customUrl.trim() && validateArcgisQueryUrl(customUrl).ok
          ? [validateArcgisQueryUrl(customUrl).normalizedUrl!]
          : undefined,
        radiusMiles,
      });

      const parts = [`Added ${result.added} layer${result.added === 1 ? '' : 's'}`];
      if (result.skipped > 0) {
        parts.push(`${result.skipped} duplicate${result.skipped === 1 ? '' : 's'} skipped`);
      }
      toast.success(parts.join(' · '));

      if (result.warnings.length > 0) {
        toast.info(result.warnings.slice(0, 3).join('; '), {
          description: result.warnings.length > 3 ? `+ ${result.warnings.length - 3} more` : undefined,
        });
      }

      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Fetch failed');
    } finally {
      setFetching(false);
    }
  };

  const previewSummary = preview
    ? Object.entries(preview.fetchedBySource)
        .map(([id, count]) => {
          const entry = ARCGIS_LAYER_CATALOG.find(e => e.id === id);
          const label = entry?.label ?? id;
          return `${count} ${label}`;
        })
        .join(', ')
    : null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60"
      onClick={onClose}
      data-testid="regional-layers-dialog-backdrop"
    >
      <div
        className="bg-background w-[calc(100vw-2rem)] max-w-[560px] max-h-[90vh] overflow-y-auto shadow-2xl border border-border"
        onClick={e => e.stopPropagation()}
        data-testid="regional-layers-dialog"
      >
        <div className="px-5 py-4 border-b border-border flex items-start gap-3">
          <div className="w-9 h-9 border border-primary/40 bg-primary/10 flex items-center justify-center shrink-0">
            <Layers className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-bold uppercase tracking-widest text-text-primary">
              Fetch regional layers
            </h2>
            <p className="text-[10px] text-text-secondary mt-0.5">
              Pull public infrastructure geometry within a radius of your site and merge into map overlays.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-text-muted hover:text-text-primary"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label
              htmlFor="regional-radius"
              className="type-body font-bold uppercase tracking-widest text-text-secondary"
            >
              Search radius
            </label>
            <select
              id="regional-radius"
              value={radiusMiles}
              onChange={e => {
                setRadiusMiles(Number(e.target.value));
                setPreview(null);
              }}
              className="mt-1 w-full border border-border bg-surface-1 px-2 py-1.5 text-xs text-text-primary"
              data-testid="regional-radius-select"
            >
              {REGIONAL_RADIUS_OPTIONS_MILES.map(mi => (
                <option key={mi} value={mi}>
                  {formatRadiusMiles(mi)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="type-body font-bold uppercase tracking-widest text-text-secondary mb-2">
              Layer catalog
            </div>
            <div className="space-y-3 max-h-[240px] overflow-y-auto border border-border-subtle p-2">
              {CATEGORY_ORDER.map(cat => {
                const entries = grouped.get(cat);
                if (!entries?.length) return null;
                return (
                  <div key={cat}>
                    <div className="type-meta font-bold uppercase tracking-widest text-text-muted mb-1">
                      {CATEGORY_LABELS[cat]}
                    </div>
                    <div className="space-y-1">
                      {entries.map(entry => (
                        <label
                          key={entry.id}
                          className="flex items-start gap-2 cursor-pointer hover:bg-state-hover px-1 py-0.5"
                          data-testid={`regional-catalog-${entry.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedIds.has(entry.id)}
                            onChange={() => toggleEntry(entry.id)}
                            className="mt-0.5 accent-primary"
                          />
                          <span className="text-[10px] text-text-primary">{entry.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="type-meta text-text-muted mt-1">
              Lit-fiber route linework is not available nationally — use KMZ or carrier quotes for fiber paths.
            </p>
          </div>

          <div>
            <label
              htmlFor="regional-custom-url"
              className="type-body font-bold uppercase tracking-widest text-text-secondary"
            >
              Custom ArcGIS URL (optional)
            </label>
            <div className="mt-1 flex gap-2">
              <input
                id="regional-custom-url"
                type="url"
                value={customUrl}
                onChange={e => {
                  setCustomUrl(e.target.value);
                  setCustomUrlValid(null);
                  setCustomUrlError(undefined);
                  setPreview(null);
                }}
                placeholder="https://services1.arcgis.com/…/FeatureServer/0/query"
                className="flex-1 border border-border bg-surface-1 px-2 py-1.5 text-xs text-text-primary font-mono"
                data-testid="regional-custom-url"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={validateCustom}
                data-testid="regional-validate-url"
              >
                Validate
              </Button>
            </div>
            {customUrlValid === true && (
              <p className="type-body text-success mt-1 flex items-center gap-1" data-testid="regional-url-valid">
                <CheckCircle2 className="w-3 h-3" /> URL is allowlisted and valid
              </p>
            )}
            {customUrlValid === false && customUrlError && (
              <p className="type-body text-danger mt-1 flex items-center gap-1" data-testid="regional-url-error">
                <AlertCircle className="w-3 h-3" /> {customUrlError}
              </p>
            )}
          </div>

          {preview && (
            <div
              className="border border-border-subtle bg-state-hover p-2 text-[10px] text-text-secondary"
              data-testid="regional-preview-summary"
            >
              <div className="font-bold uppercase tracking-widest type-body text-text-muted mb-1">
                Preview
              </div>
              {previewSummary || 'No features found for the current selection'}
              {preview.warnings.length > 0 && (
                <ul className="mt-1 type-body text-warning list-disc pl-4">
                  {preview.warnings.slice(0, 5).map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-border flex flex-wrap items-center justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={fetching}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={runPreview}
            disabled={previewLoading || fetching}
            data-testid="regional-preview-btn"
          >
            {previewLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            Preview
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleFetch}
            disabled={fetching || previewLoading}
            data-testid="regional-fetch-btn"
          >
            {fetching ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            Fetch &amp; merge
          </Button>
        </div>
      </div>
    </div>
  );
}

export { sourceLabel as regionalLayerSourceLabel };
